import { faArrowRight } from "@fortawesome/pro-regular-svg-icons";

import { toPhone } from "common/utils/formatting";
import LinkButton, { LinkButtonProps } from "common/components/LinkButton";
import Icon from "common/components/Icon";

import { useClinic } from "client/clinics/hooks";
import { ModalStatus } from "client/common/types";
import { Clinic } from "client/clinics/types";
import { INITIAL_CLIENT_DATA } from "client/common/utils";

import SupportIcon from "./SupportIcon";
import { SetModalVisibility, SupportTileEnum } from "../types";
import { useSupportChat } from "../hooks";

import styles from "../styles/SupportTile.module.less";

type SupportTileBoxProps = {
  children: React.ReactNode;
  title: React.ReactNode;
  isLink?: boolean;
} & ({ isLink: false } | LinkButtonProps);

const SupportTileBox = ({ children, title, isLink = true, ...linkProps }: SupportTileBoxProps) => {
  return (
    <LinkButton
      className={`${styles.tile} ${isLink ? styles.isLink : ""}`}
      {...(linkProps as LinkButtonProps)}
    >
      <div className={styles.hexContainer}>
        <SupportIcon />
        <SupportIcon.Background className={styles.background} />
      </div>
      <div className={styles.contentArrow}>
        <div className={styles.content}>
          <div className={styles.title}>{title}</div>
          <div className={styles.body}>{children}</div>
        </div>
        {isLink && <Icon className={styles.arrow} icon={faArrowRight} />}
      </div>
    </LinkButton>
  );
};

const ClinicHours = ({ clinic }: { clinic: Clinic }) => {
  return clinic.daily_hours ? (
    <div>{clinic.daily_hours}</div>
  ) : (
    <div>
      {`Hours are unavailable. Please contact ${clinic.display_name} at `}
      <LinkButton href={`tel:${clinic.phone_number}`} className={styles.link}>
        {toPhone(clinic.phone_number)}
      </LinkButton>
      {" for information about clinic hours."}
    </div>
  );
};

const exhaustiveCheck = (kind: never) => {
  console.warn(`Invalid support tile kind ${kind}`);
  return null;
};

type SupportTileProps = { kind: SupportTileEnum; onSetModalVisibility: SetModalVisibility };

const SupportTile = ({ kind, onSetModalVisibility }: SupportTileProps) => {
  const clinic = useClinic();
  const { vetcove_support_phone: supportPhone, vetcove_support_email: supportEmail } =
    INITIAL_CLIENT_DATA;
  const chat = useSupportChat();

  switch (kind) {
    case SupportTileEnum.ClinicPhone:
      return (
        <SupportTileBox
          title={`Call ${clinic.display_name}`}
          href={`tel:${clinic.phone_number}`}
          newTab={false}
        >
          <div>Phone call</div>
          <div>{toPhone(clinic.phone_number)}</div>
        </SupportTileBox>
      );
    case SupportTileEnum.ClinicSms:
      return clinic.communication_number ? (
        <SupportTileBox
          title={`Message ${clinic.display_name}`}
          href={`sms:${clinic.communication_number.number}`}
          newTab={false}
        >
          <div>SMS message</div>
          <div>{toPhone(clinic.communication_number.number)}</div>
        </SupportTileBox>
      ) : null;
    case SupportTileEnum.ClinicEmail:
      return clinic.email ? (
        <SupportTileBox
          title={`Email ${clinic.display_name}`}
          href={`mailto:${clinic.email}`}
          newTab={false}
        >
          <div>Email</div>
          <div>{clinic.email}</div>
        </SupportTileBox>
      ) : null;
    case SupportTileEnum.ClinicHours:
      return (
        <SupportTileBox title={`${clinic.display_name} hours`} isLink={false}>
          <ClinicHours clinic={clinic} />
        </SupportTileBox>
      );
    case SupportTileEnum.VetcovePhone:
      return (
        <SupportTileBox title="Home delivery support" href={`tel:${supportPhone}`} newTab={false}>
          <div>Phone call</div>
          <div>{toPhone(supportPhone)}</div>
        </SupportTileBox>
      );
    case SupportTileEnum.VetcoveSms:
      return (
        <SupportTileBox title="Home delivery support" href={`sms:${supportPhone}`} newTab={false}>
          <div>SMS message</div>
          <div>{toPhone(supportPhone)}</div>
        </SupportTileBox>
      );
    case SupportTileEnum.VetcoveEmail:
      return (
        <SupportTileBox
          title="Home delivery support"
          href={`mailto:${supportEmail}`}
          newTab={false}
        >
          <div>Email</div>
          <div>{supportEmail}</div>
        </SupportTileBox>
      );
    case SupportTileEnum.Chat:
      return (
        <SupportTileBox title="Chat" onClick={() => chat.show()}>
          <div>Access our live chat</div>
        </SupportTileBox>
      );
    case SupportTileEnum.OrderStatus:
      return (
        <SupportTileBox
          title="Check Order Status"
          onClick={() => onSetModalVisibility("orderStatusModalIsVisible", ModalStatus.Open)}
        >
          <div>View tracking information</div>
        </SupportTileBox>
      );
    default:
      return exhaustiveCheck(kind);
  }
};

export default SupportTile;
